import React from "react";
import uniq from "lodash/uniq";
import { User } from "~/common/Store/models/User";
import { QuantityDiscount } from "../ProductDetailPageResult";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import LotPricingPopover from "./LotPricingPopover";
import shouldDisplayBelowMapPrice from "./shouldDisplayBelowMapPrice";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import getMinBuyForQuantityDropdown from "./AddToCart/getMinBuyForQuantityDropdown";
import isActiveSale from "./isActiveSale";
interface Props {
  product: PricingProduct;
  user: User;
  cheapestQuantityDiscount?: QuantityDiscount;
  isGoogleView?: boolean;
}
const PriceLabel = ({
  product,
  user,
  cheapestQuantityDiscount,
  isGoogleView
}: Props) => {
  const {
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    minimumBuy,
    mustBuy,
    price,
    unitOfMeasure,
    groupingAttributes
  } = product;
  const displayBelowMapPrice = shouldDisplayBelowMapPrice(product, user);
  const displayRequiredCustomization = shouldDisplayRequiredCustomization(product);
  const mapStyleG = price.minimumAdvertisedPriceProperties?.type?.trim() === "G";
  const displayCustomQuoteText = price.displayMinimumAdvertisedPrice && displayBelowMapPrice && user && !mapStyleG;
  const getQuantityDiscountLabel = (): JSX.Element => {
    let text = "Only";
    if (isGoogleView && cheapestQuantityDiscount) {
      const updatedMinBuy = getMinBuyForQuantityDropdown({
        minBuy: minimumBuy,
        mustBuy
      });
      text = `Buy ${updatedMinBuy} or more`;
    } else if (cheapestQuantityDiscount?.isLotDiscount) {
      text = `Buy in lots of ${cheapestQuantityDiscount.quantity}: `;
    } else if (cheapestQuantityDiscount) {
      text = `Buy ${cheapestQuantityDiscount.quantity} or more`;
    }
    return <label data-sentry-component="getQuantityDiscountLabel" data-sentry-source-file="PriceLabel.tsx">
        {text}
        {!isGoogleView && cheapestQuantityDiscount?.isLotDiscount && <LotPricingPopover />}
      </label>;
  };
  const getCustomQuoteLabel = () => {
    if (showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers && user.hasWebPlus) {
      return <label>Exclusive Plus Pricing</label>;
    }
    if (showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers && user.platinum?.enabled) {
      return <label>Exclusive Platinum Pricing</label>;
    }
    return <label className="to-see-label" data-sentry-component="getCustomQuoteLabel" data-sentry-source-file="PriceLabel.tsx">
        {" Custom Quote for "}
        <span className="capitalize">
          {user.companyName ? user.companyName : user.fullName}
        </span>
      </label>;
  };
  if (displayRequiredCustomization) {
    if (isGoogleView) {
      return <label>Buy 1 or more</label>;
    }
    return <label>Custom Prices Starting at:</label>;
  }
  if (displayCustomQuoteText) {
    return <>
        {getCustomQuoteLabel()}
        {cheapestQuantityDiscount && getQuantityDiscountLabel()}
      </>;
  }
  if (cheapestQuantityDiscount) {
    return getQuantityDiscountLabel();
  }
  let label = unitOfMeasure?.toLocaleLowerCase() === "each" ? "Only" : "Each only";
  if (isGoogleView && (minimumBuy > 1 || mustBuy > 1)) {
    label = "From";
  }
  const showFrom = groupingAttributes.isGroupMaster && uniq(groupingAttributes.groupMembers.map(x => x.price.price)).length > 1;
  if (showFrom) {
    return <span className="from">From</span>;
  }
  return isActiveSale(price) ? null : <label data-sentry-component="PriceLabel" data-sentry-source-file="PriceLabel.tsx">{label}</label>;
};
export default PriceLabel;